import React from 'react';

//helpers
import {styleable} from '../../decorators';

//styled-components
import {Text} from './styles';
import type {TextColor, TextPlatform, TextType} from './styles';

type TextBaseProps = {
  children: React.ReactNode;
  device: 'desktop' | 'mobile';
  ellipsis?: boolean;
  opacity?: number;
  secondary?: boolean;
  size?: 'small' | 'normal' | 'large';
  tag?: React.ElementType;
};

const TextBase = styleable(
  ({
    children,
    device,
    ellipsis,
    opacity,
    secondary = false,
    size = 'normal',
    tag = 'div',
    ...forwardedProps
  }: TextBaseProps) => {
    const Tag = Text.withComponent(tag);
    return (
      <Tag ellipsis={ellipsis} device={device} opacity={opacity} secondary={secondary} size={size} {...forwardedProps}>
        {children}
      </Tag>
    );
  }
) as unknown as React.ComponentType<any>;

export const TextDesktop = styleable(props => <TextBase device="desktop" {...props} />);

export const TextMobile = styleable(props => <TextBase device="mobile" {...props} />);

export type TypographyProps<T extends React.ElementType = 'span'> = {
  as?: T;
  type?: TextType;
  platform?: TextPlatform;
  color?: TextColor;
  ellipsis?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, 'as' | 'type' | 'platform' | 'color' | 'style' | 'children'>;

/**
 * Renders text with a type scale and a palette color.
 *
 * Use it directly and put layout on a container around it. Do not wrap it in
 * styled(): styled-components takes the `as` prop for itself and would render
 * the bare tag instead of Typography, dropping the type scale and color.
 */
export const Typography = <T extends React.ElementType = 'span'>({
  as,
  type = 'generalBody',
  platform = 'desktop',
  color,
  style,
  children,
  ...rest
}: TypographyProps<T>) => {
  const Tag: React.ElementType = as ?? 'span';
  return (
    <Text as={Tag} textType={type} platform={platform} textColor={color} style={style} {...rest}>
      {children}
    </Text>
  );
};
